class UgcStories extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement[]} */
    this.cards = [];
    /** @type {boolean} */
    this.mobileFullscreen = false;
    /** @type {boolean} */
    this.desktopHover = false;
    /** @type {HTMLVideoElement | null} */
    this.activeVideo = null;
    /** @type {HTMLElement | null} */
    this.expandedCard = null;
    /** @type {IntersectionObserver | null} */
    this.io = null;
    /** @type {HTMLElement | null} */
    this.expandedOriginalParent = null;
    /** @type {Node | null} */
    this.expandedOriginalNextSibling = null;
  }

  connectedCallback() {
    if (this.dataset.ugcReady === 'true') return;
    this.dataset.ugcReady = 'true';

    const storyCards = Array.from(this.querySelectorAll('[data-ugc-story]'));
    this.cards = /** @type {HTMLElement[]} */ (storyCards.filter((element) => element instanceof HTMLElement));
    this.mobileFullscreen = this.dataset.mobileFullscreen === 'true';
    this.desktopHover = this.dataset.desktopHover === 'true';

    this.setupLazyLoading();
    this.bindEvents();
  }

  setupLazyLoading() {
    const supportsObserver = 'IntersectionObserver' in window;
    /**
     * @param {HTMLVideoElement | null} video
     */
    const loadVideo = (video) => {
      if (!video || video.dataset.loaded === 'true') return;
      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach((sourceEl) => {
        const source = /** @type {HTMLSourceElement} */ (sourceEl);
        const sourceUrl = source.dataset.src;
        if (!sourceUrl) return;
        source.src = sourceUrl;
        source.removeAttribute('data-src');
      });
      video.load();
      video.dataset.loaded = 'true';
    };

    if (!supportsObserver) {
      this.cards.forEach((card) => {
        const video = /** @type {HTMLVideoElement | null} */ (card.querySelector('video'));
        loadVideo(video);
      });
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = /** @type {HTMLVideoElement | null} */ (entry.target.querySelector('video'));
          if (!video) return;

          if (entry.isIntersecting) {
            loadVideo(video);
          } else if (!video.paused) {
            this.pauseVideo(video, /** @type {HTMLElement} */ (entry.target));
          }
        });
      },
      { rootMargin: '240px 0px' }
    );

    const io = this.io;
    if (!io) return;
    this.cards.forEach((card) => io.observe(card));
  }

  bindEvents() {
    this.cards.forEach((card) => {
      const video = /** @type {HTMLVideoElement | null} */ (card.querySelector('video'));
      const playButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-play]'));
      const muteButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-mute]'));
      const closeButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-close]'));

      if (!video) return;

      const bindMuteControl = () => {
        if (!muteButton) return;
        const onMeta = () => this.syncMuteControl(video, muteButton);
        if (video.readyState >= 1) onMeta();
        else video.addEventListener('loadedmetadata', onMeta, { once: true });
        video.addEventListener('volumechange', () => this.syncMuteControl(video, muteButton));
      };
      bindMuteControl();

      const bindPlayControl = () => {
        if (!playButton) return;
        const sync = () => this.syncPlayControl(video, playButton);
        sync();
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
      };
      bindPlayControl();

      video.addEventListener('play', () => {
        card.classList.add('is-active');
      });

      video.addEventListener('pause', () => {
        card.classList.remove('is-active');
      });

      if (this.desktopHover) {
        card.addEventListener('mouseenter', () => {
          if (!this.isDesktopPointer()) return;
          this.playVideo(video, card, { expandOnMobile: false, allowUnmute: false });
        });

        card.addEventListener('mouseleave', () => {
          if (!this.isDesktopPointer()) return;
          this.pauseVideo(video, card, true);
          video.muted = true;
          if (muteButton) this.syncMuteControl(video, muteButton);
        });
      }

      card.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('[data-ugc-shop]')) return;
        if (target.closest('[data-ugc-mute]')) return;
        if (target.closest('[data-ugc-close]')) return;
        if (this.isDesktopPointer()) return;

        event.preventDefault();

        if (!video.paused) {
          this.pauseVideo(video, card);
          if (card.classList.contains('is-expanded')) {
            this.closeMobileFullscreen(card);
          }
          return;
        }

        this.playVideo(video, card, { expandOnMobile: this.mobileFullscreen });
      });

      if (playButton) {
        playButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!video.paused) {
            this.pauseVideo(video, card);
            return;
          }
          this.playVideo(video, card, { expandOnMobile: this.mobileFullscreen && !this.isDesktopPointer() });
        });
      }

      if (muteButton) {
        muteButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!this.videoLikelyHasAudio(video)) return;
          video.muted = !video.muted;
          this.syncMuteControl(video, muteButton);
        });
      }

      if (closeButton) {
        closeButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closeMobileFullscreen(card);
          this.pauseVideo(video, card);
        });
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAll();
    });

    document.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement) return;
      if (this.expandedCard) {
        this.expandedCard.classList.remove('is-expanded');
        document.documentElement.style.overflow = '';
        this.expandedCard = null;
      }
    });
  }

  isDesktopPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * @param {HTMLVideoElement | null} [exceptVideo]
   */
  pauseAll(exceptVideo = null) {
    this.cards.forEach((card) => {
      const video = /** @type {HTMLVideoElement | null} */ (card.querySelector('video'));
      if (!video || video === exceptVideo) return;
      this.pauseVideo(video, card);
      if (card.classList.contains('is-expanded')) {
        this.closeMobileFullscreen(card);
      }
    });
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement} card
   * @param {{ expandOnMobile?: boolean; allowUnmute?: boolean }} [options]
   */
  async playVideo(video, card, { expandOnMobile = false, allowUnmute = true } = {}) {
    this.pauseAll(video);
    this.ensureLoaded(video);

    if (expandOnMobile && !this.isDesktopPointer()) {
      await this.openMobileFullscreen(card);
    }

    try {
      // Hover is not a user gesture: unmuted play() is rejected. Keep muted for desktop hover preview.
      if (allowUnmute) {
        await this.unmuteIfAudioPresent(video);
      } else {
        video.muted = true;
      }
      await video.play();
      this.activeVideo = video;
      card.classList.add('is-active');
      const muteBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-mute]'));
      if (muteBtn) this.syncMuteControl(video, muteBtn);
      const playBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-play]'));
      if (playBtn) this.syncPlayControl(video, playBtn);
    } catch (_) {
      card.classList.remove('is-active');
      const playBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-play]'));
      if (playBtn) this.syncPlayControl(video, playBtn);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement} card
   * @param {boolean} [reset]
   */
  pauseVideo(video, card, reset = false) {
    video.pause();
    card.classList.remove('is-active');
    if (reset && Number.isFinite(video.duration)) {
      video.currentTime = 0;
    }
    if (this.activeVideo === video) {
      this.activeVideo = null;
    }
    const muteBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-mute]'));
    if (muteBtn) this.syncMuteControl(video, muteBtn);
    const playBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-ugc-play]'));
    if (playBtn) this.syncPlayControl(video, playBtn);
  }

  /**
   * @param {HTMLVideoElement} video
   */
  ensureLoaded(video) {
    if (video.dataset.loaded === 'true') return;
    video.querySelectorAll('source[data-src]').forEach((sourceEl) => {
      const source = /** @type {HTMLSourceElement} */ (sourceEl);
      const sourceUrl = source.dataset.src;
      if (!sourceUrl) return;
      source.src = sourceUrl;
      source.removeAttribute('data-src');
    });
    video.load();
    video.dataset.loaded = 'true';
  }

  /**
   * Wait until dimensions / tracks are known so we can detect an audio track.
   * @param {HTMLVideoElement} video
   */
  async ensureMetadataLoaded(video) {
    if (video.readyState >= 1) return;
    await new Promise((resolve) => {
      video.addEventListener('loadedmetadata', resolve, { once: true });
    });
  }

  /**
   * Best-effort: true if the file likely has an audio track; false if clearly silent.
   * @param {HTMLVideoElement} video
   */
  videoLikelyHasAudio(video) {
    const v = /** @type {HTMLVideoElement & { mozHasAudio?: boolean; audioTracks?: { length: number } }} */ (video);
    if (typeof v.mozHasAudio === 'boolean') return v.mozHasAudio;
    try {
      const tracks = v.audioTracks;
      if (tracks && tracks.length > 0) return true;
      if (tracks && tracks.length === 0) return false;
    } catch (_) {
      /* ignore */
    }
    return true;
  }

  /**
   * After user gesture, unmute only when the media appears to carry audio.
   * @param {HTMLVideoElement} video
   */
  async unmuteIfAudioPresent(video) {
    await this.ensureMetadataLoaded(video);
    if (this.videoLikelyHasAudio(video)) video.muted = false;
  }

  /**
   * Show mute toggle only when the file has an audio track; sync icon and labels.
   * @param {HTMLVideoElement} video
   * @param {HTMLButtonElement} button
   */
  syncMuteControl(video, button) {
    if (!this.videoLikelyHasAudio(video)) {
      button.hidden = true;
      return;
    }
    button.hidden = false;
    const unmuted = !video.muted;
    button.classList.toggle('is-unmuted', unmuted);
    button.setAttribute('aria-label', unmuted ? 'Mute' : 'Turn sound on');
  }

  /**
   * Sync play/pause icon and aria-label with video state.
   * @param {HTMLVideoElement} video
   * @param {HTMLButtonElement} button
   */
  syncPlayControl(video, button) {
    const playing = !video.paused;
    button.classList.toggle('is-playing', playing);
    button.setAttribute('aria-label', playing ? 'Pause story' : 'Play story');
  }

  /**
   * @param {HTMLElement} card
   */
  async openMobileFullscreen(card) {
    if (this.expandedCard && this.expandedCard !== card) {
      this.closeMobileFullscreen(this.expandedCard);
    }

    if (card.classList.contains('is-expanded')) return;

    this.expandedCard = card;
    card.classList.add('is-expanded');

    if (card.parentElement !== document.body) {
      this.expandedOriginalParent = card.parentElement;
      this.expandedOriginalNextSibling = card.nextSibling;
      document.body.appendChild(card);
    }

    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('ugc-fullscreen-open');
  }

  /**
   * @param {HTMLElement | null} card
   */
  closeMobileFullscreen(card) {
    if (!card) return;

    card.classList.remove('is-expanded');

    const originalParent = this.expandedOriginalParent;
    if (originalParent) {
      const nextSibling = this.expandedOriginalNextSibling;
      if (nextSibling && nextSibling.parentNode === originalParent) {
        originalParent.insertBefore(card, nextSibling);
      } else {
        originalParent.appendChild(card);
      }
    }

    this.expandedOriginalParent = null;
    this.expandedOriginalNextSibling = null;

    if (this.expandedCard === card) {
      this.expandedCard = null;
      document.documentElement.style.overflow = '';
      document.body.classList.remove('ugc-fullscreen-open');
    }
  }
}

if (!customElements.get('ugc-stories')) {
  customElements.define('ugc-stories', UgcStories);
}
